/* eslint-disable react/display-name */
import React, {
	useCallback, useEffect, useMemo, useState
} from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Badge,
	Button,
	Card,
	Col,
	Drawer,
	Dropdown,
	Empty,
	Form,
	Input,
	Menu,
	message,
	Modal,
	Popconfirm,
	Progress,
	Row,
	Select,
	Space,
	Spin,
	Statistic,
	Table,
	Tabs,
	Tag,
	Tooltip,
	Timeline,
	Typography
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import {
	CheckCircleOutlined,
	ClockCircleOutlined,
	DeleteOutlined,
	DownOutlined,
	EllipsisOutlined,
	ExclamationCircleOutlined,
	FieldTimeOutlined,
	PlusOutlined,
	ReloadOutlined,
	SyncOutlined
} from '@ant-design/icons'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { RootState } from '../../reducers'
import { getReq } from '../../utils/request'
import { parseWeekDays } from './recurringGhostEvents'
import {
	addManagerTaskComment,
	assignManagerTaskLabelEntity,
	CalendarGhostEvent,
	completeManagerTask,
	createManagerTask,
	deleteManagerTask,
	generateRecurringManagerTasks,
	getManagerTask,
	getManagerTaskLabels,
	getManagerTasks,
	getManagerTasksCalendar,
	getRecurringTaskTemplates,
	markOverdueManagerTasks,
	updateManagerTask,
	updateRecurringTaskTemplate
} from './managerTaskApi'
import RecurringTemplateManager from './RecurringTemplateManager'
import TaskFilterBar, { TaskFilters, EMPTY_TASK_FILTERS } from './TaskFilterBar'
import LabelPickerPopover from './LabelPickerPopover'
import DatePickerPopover from './DatePickerPopover'
import SimplePickerPopover from './SimplePickerPopover'
import PenalizaciaPopover from './PenalizaciaPopover'
import {
	MANAGER_TASK_EVALUATION_STATUS,
	MANAGER_TASK_FREQUENCY,
	MANAGER_TASK_PRIORITY,
	MANAGER_TASK_WORKFLOW_STATUS,
	ManagerRecurringTaskTemplate,
	ManagerTask,
	ManagerTaskLabel,
	ManagerTaskLabelAssignmentData,
	ManagerTaskPayload,
	ManagerTaskRecurrenceIntent,
	ManagerTaskUser,
	ManagerRecurringTaskTemplatePayload,
	RecurringPattern,
	WeekDayKey
} from './types'

type AssigneeOption = {
	id: number
	name: string
	role: string
}

const PAGE_STYLE: React.CSSProperties = {
	background: '#f3f4f6',
	minHeight: '100%',
	padding: '24px 28px'
}

const CARD_STYLE: React.CSSProperties = {
	border: '1px solid #e5e7eb',
	borderRadius: 8,
	boxShadow: '0 2px 8px rgba(15, 23, 42, 0.06)'
}

const STATUS_LABELS: Record<MANAGER_TASK_WORKFLOW_STATUS, string> = {
	TODO: 'Na spracovanie',
	IN_PROGRESS: 'Rozpracované',
	DONE: 'Hotové',
	VOIDED: 'Zrušené'
}

const STATUS_COLORS: Record<MANAGER_TASK_WORKFLOW_STATUS, string> = {
	TODO: 'default',
	IN_PROGRESS: 'blue',
	DONE: 'green',
	VOIDED: 'red'
}

const PRIORITY_LABELS: Record<MANAGER_TASK_PRIORITY, string> = {
	LOW: 'Bežná',
	MEDIUM: 'Stredná',
	HIGH: 'Vysoká',
	CRITICAL: 'Prioritná'
}

const PRIORITY_ICON_DATA: Record<MANAGER_TASK_PRIORITY, { symbol: string; color: string }> = {
	CRITICAL: { symbol: '⊖', color: '#cf1322' },
	HIGH: { symbol: '⌃', color: '#f5222d' },
	MEDIUM: { symbol: '=', color: '#fa8c16' },
	LOW: { symbol: '⌄', color: '#1890ff' }
}

const PriorityIcon = ({ priority }: { priority: MANAGER_TASK_PRIORITY }) => {
	const { symbol, color } = PRIORITY_ICON_DATA[priority]
	return <span style={{ color, fontWeight: 700, fontSize: 13, marginRight: 4, lineHeight: 1 }}>{symbol}</span>
}

const PRIORITY_COLORS: Record<MANAGER_TASK_PRIORITY, string> = {
	LOW: 'default',
	MEDIUM: 'blue',
	HIGH: 'orange',
	CRITICAL: 'red'
}

export const EVALUATION_LABELS: Record<MANAGER_TASK_EVALUATION_STATUS, string> = {
	PENDING: 'Čaká',
	SUCCESS: 'Včas',
	FAILED: 'Po termíne'
}

const EVALUATION_COLORS: Record<MANAGER_TASK_EVALUATION_STATUS, string> = {
	PENDING: 'default',
	SUCCESS: 'green',
	FAILED: 'red'
}

const nameOf = (user?: ManagerTaskUser | null) => {
	if (!user) return '-'
	return [user.lastName, user.firstName].filter(Boolean).join(' ') || user.email || `#${user.id}`
}

export const RECURRING_TASK_TOOLTIP = 'Opakujúca sa úloha'

export const RECURRENCE_PILL_LABELS: Record<'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY_DAY', string> = {
	DAILY: 'Denne', // pill label: ↻ Denne
	WEEKDAYS: 'Pracovné dni',
	WEEKLY: 'Týždenne',
	MONTHLY_DAY: 'Mesačne'
}

const DAY_SK: Record<string, string> = {
	SUN: 'Ne', MON: 'Po', TUE: 'Ut', WED: 'St', THU: 'Št', FRI: 'Pi', SAT: 'So'
}

const formatWeekDaysSK = (days: string[] | string | null | undefined): string => parseWeekDays(Array.isArray(days) ? days.join(',') : days).map((d) => DAY_SK[d] || d).join(', ')

export const isRecurringTask = (task: ManagerTask) => task.type === 'RECURRING' || Boolean(task.recurringTemplateId)

const RecurringTaskIcon = () => (
	<Tooltip title={RECURRING_TASK_TOOLTIP}>
		<SyncOutlined style={{ color: '#337ab7', marginLeft: 6 }} />
	</Tooltip>
)

export const formatDate = (value?: string | null) => (value ? moment(value).format('DD.MM.YYYY HH:mm') : 'Bez termínu')

const ACTIVITY_ACTION_LABELS: Record<string, string> = {
	// Úlohy
	TASK_CREATED: 'Úloha vytvorená',
	TASK_UPDATED: 'Aktualizácia úlohy',
	TASK_COMPLETED: 'Úloha dokončená',
	TASK_VOIDED: 'Úloha zrušená',
	TASK_DELETED: 'Úloha vymazaná',
	TASK_OVERDUE: 'Úloha po termíne',
	// Stav
	STATUS_CHANGED: 'Zmena stavu',
	ADMIN_OVERRIDE: 'Zmena administrátorom',
	// Komentáre
	COMMENT_ADDED: 'Komentár pridaný',
	COMMENT_CREATED: 'Komentár pridaný',
	COMMENT_DELETED: 'Komentár odstránený',
	// Opakovanie
	RECURRING_TASK_GENERATED: 'Úloha vygenerovaná zo šablóny',
	SYSTEM_TASK_CREATED: 'Systémová úloha vytvorená',
	RECURRING_TEMPLATE_CREATED: 'Šablóna opakovania vytvorená',
	RECURRING_TEMPLATE_UPDATED: 'Šablóna opakovania aktualizovaná',
	RECURRING_TEMPLATE_DEACTIVATED: 'Šablóna opakovania deaktivovaná'
}

const ACTIVITY_FIELD_LABELS: Record<string, string> = {
	title: 'Názov',
	description: 'Popis',
	workflowStatus: 'Stav',
	priority: 'Priorita',
	assignedToUserId: 'Priradený',
	deadlineAt: 'Termín',
	startAt: 'Začiatok',
	penaltyAmount: 'Penalizácia',
	labelIds: 'Labels',
	recurringPattern: 'Opakovanie',
	reminderOffsetHours: 'Upozornenie (h)'
}

const translateActivityValue = (field: string, value: any): string => {
	if (value === null || value === undefined) return '—'
	if (field === 'workflowStatus') return STATUS_LABELS[value as MANAGER_TASK_WORKFLOW_STATUS] ?? value
	if (field === 'priority') return PRIORITY_LABELS[value as MANAGER_TASK_PRIORITY] ?? value
	if (field === 'deadlineAt' || field === 'startAt') return value ? moment(value).format('DD.MM.YYYY') : '—'
	if (field === 'labelIds') return Array.isArray(value) ? `${value.length} label(s)` : String(value)
	if (typeof value === 'boolean') return value ? 'Áno' : 'Nie'
	return String(value)
}

const renderActivityDetail = (action: string, metadata: Record<string, any> | null | undefined): string | null => {
	if (!metadata) return null
	if (action === 'STATUS_CHANGED' && metadata.workflowStatus) {
		return `→ ${STATUS_LABELS[metadata.workflowStatus as MANAGER_TASK_WORKFLOW_STATUS] ?? metadata.workflowStatus}`
	}
	if ((action === 'TASK_UPDATED' || action === 'ADMIN_OVERRIDE') && metadata.changedFields) {
		const changes = metadata.changedFields as Record<string, { from?: any; to?: any }>
		const parts = Object.entries(changes).map(([field, change]) => {
			const label = ACTIVITY_FIELD_LABELS[field] ?? field
			const toVal = translateActivityValue(field, change.to)
			if (change.from !== undefined) {
				const fromVal = translateActivityValue(field, change.from)
				return `${label}: ${fromVal} → ${toVal}`
			}
			return `${label}: → ${toVal}`
		})
		return parts.join(' · ') || null
	}
	if (action === 'TASK_COMPLETED' && metadata.evaluationStatus) {
		const evalLabel = metadata.evaluationStatus === 'SUCCESS' ? 'Včas' : 'Po termíne'
		return `Výsledok: ${evalLabel}`
	}
	if ((action === 'RECURRING_TEMPLATE_CREATED' || action === 'RECURRING_TEMPLATE_UPDATED') && metadata.changedFields) {
		const changes = metadata.changedFields as Record<string, { from?: any; to?: any }>
		const parts = Object.entries(changes).map(([field, change]) => {
			const label = ACTIVITY_FIELD_LABELS[field] ?? field
			const toVal = translateActivityValue(field, change.to)
			if (change.from !== undefined) {
				const fromVal = translateActivityValue(field, change.from)
				return `${label}: ${fromVal} → ${toVal}`
			}
			return `${label}: → ${toVal}`
		})
		return parts.join(' · ') || null
	}
	if (action === 'RECURRING_TEMPLATE_DEACTIVATED') {
		return 'Opakovanie deaktivované'
	}
	return null
}

// Helper: determines whether to show the fixed-completion warning icon (D-20)
const shouldShowFixedCompletionIcon = (task: ManagerTask): boolean => task.fixedCompletion === true && (
	task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED
	|| task.predecessorTaskId != null
)

// Calendar React Query key — shared prefix so invalidateQueries covers all cached months
const CALENDAR_QUERY_KEY = 'managerTasksCalendar'
const calendarQueryKey = (month: string) => [CALENDAR_QUERY_KEY, month] as const

const SK_MONTHS = ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December']
const MAX_CALENDAR_VISIBLE_ITEMS_PER_DAY = 6

interface CalendarViewProps {
	onOpenTask: (task: ManagerTask) => void
	onGhostClick: (ghost: CalendarGhostEvent) => void
	assigneeId?: number | null
}

const CalendarView: React.FC<CalendarViewProps> = React.memo(({ onOpenTask, onGhostClick, assigneeId }) => {
	const [calendarMonth, setCalendarMonth] = useState(() => moment().startOf('month'))

	// Use string key as dep — Moment objects are mutable and cause useMemo/useEffect to malfunction
	const calendarMonthKey = calendarMonth.format('YYYY-MM')

	// keepPreviousData: true so switching months doesn't blank the grid before new data arrives
	const { data, isFetching } = useQuery({
		queryKey: calendarQueryKey(calendarMonthKey),
		queryFn: () => getManagerTasksCalendar(calendarMonthKey),
		keepPreviousData: true
	})
	const allTasks = data?.tasks ?? []
	const allGhosts = data?.ghosts ?? []
	const tasks = assigneeId ? allTasks.filter((t) => t.assignedToUserId === assigneeId) : allTasks
	const ghosts = assigneeId ? allGhosts : allGhosts

	const today = moment()

	// Array.from avoids useMemo with Moment object dep — safe to recompute per render (O(42))
	// Start grid on Monday: day() returns 0=Sun,1=Mon,...,6=Sat; Sun needs 6 days back, others need (day-1)
	const firstOfMonth = calendarMonth.clone().startOf('month')
	const dow = firstOfMonth.day() // 0=Sun … 6=Sat
	const daysBack = dow === 0 ? 6 : dow - 1
	const gridStart = firstOfMonth.clone().subtract(daysBack, 'days')
	const days = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, 'days'))

	const tasksByDate = useMemo(() => {
		const grouped: Record<string, ManagerTask[]> = {}
		tasks.forEach((task) => {
			if (!task.deadlineAt) return
			const key = moment(task.deadlineAt).format('YYYY-MM-DD')
			if (!grouped[key]) grouped[key] = []
			grouped[key].push(task)
		})
		return grouped
	}, [tasks])

	const ghostsByDate = useMemo(() => {
		const grouped: Record<string, CalendarGhostEvent[]> = {}
		ghosts.forEach((ghost) => {
			if (!grouped[ghost.occurrenceDate]) grouped[ghost.occurrenceDate] = []
			grouped[ghost.occurrenceDate].push(ghost)
		})
		return grouped
	}, [ghosts])

	const weeks: moment.Moment[][] = []
	for (let i = 0; i < days.length; i += 7) {
		weeks.push(days.slice(i, i + 7))
	}

	const DAY_HEADERS = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']

	return (
		<Spin spinning={isFetching}>
			<div style={{ userSelect: 'none' }}>
				<div style={{
					display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16
				}}>
					<Button size={'small'} onClick={() => setCalendarMonth(moment().startOf('month'))}>Dnes</Button>
					<Button size={'small'} onClick={() => setCalendarMonth((m) => m.clone().subtract(1, 'month'))}>{'‹'}</Button>
					<Button size={'small'} onClick={() => setCalendarMonth((m) => m.clone().add(1, 'month'))}>{'›'}</Button>
					<Typography.Text strong style={{ fontSize: 16 }}>
						{SK_MONTHS[calendarMonth.month()]} {calendarMonth.year()}
					</Typography.Text>
				</div>

				<div style={{
					display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e5e7eb', marginBottom: 0
				}}>
					{DAY_HEADERS.map((h) => (
						<div
							key={h}
							style={{
								textAlign: 'center', padding: '4px 0', fontSize: 12, fontWeight: 600, color: '#6b7280'
							}}
						>
							{h}
						</div>
					))}
				</div>

				{weeks.map((week, wi) => (
					<div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e5e7eb' }}>
						{week.map((day) => {
							const isCurrentMonth = day.month() === calendarMonth.month()
							const isToday = day.isSame(today, 'day')
							const dateKey = day.format('YYYY-MM-DD')
							const dayTasks = tasksByDate[dateKey] || []
							const dayGhosts = ghostsByDate[dateKey] || []
							const visibleTasks = dayTasks.slice(0, MAX_CALENDAR_VISIBLE_ITEMS_PER_DAY)
							const visibleGhostSlots = Math.max(0, MAX_CALENDAR_VISIBLE_ITEMS_PER_DAY - visibleTasks.length)
							const visibleGhosts = dayGhosts.slice(0, visibleGhostSlots)
							const hiddenTaskCount = dayTasks.length - visibleTasks.length
							const hasHiddenGhosts = dayGhosts.length > visibleGhosts.length

							// eslint-disable-next-line no-nested-ternary
							const dayNumColor = isToday ? '#fff' : isCurrentMonth ? '#1f2937' : '#9ca3af'
							return (
								<div
									key={dateKey}
									style={{
										minHeight: 90,
										padding: '4px 6px',
										background: isCurrentMonth ? '#ffffff' : '#f9fafb',
										borderRight: '1px solid #e5e7eb'
									}}
								>
									<div style={{ marginBottom: 4, textAlign: 'right' }}>
										<span style={{
											display: 'inline-flex',
											alignItems: 'center',
											justifyContent: 'center',
											width: 24,
											height: 24,
											borderRadius: '50%',
											fontSize: 12,
											fontWeight: isToday ? 700 : 400,
											background: isToday ? '#1890ff' : 'transparent',
											color: dayNumColor
										}}>
											{day.date()}
										</span>
									</div>

									{visibleTasks.map((task) => {
										const dotColor = PRIORITY_ICON_DATA[task.priority]?.color || '#8c8c8c'
										const timeLabel = task.deadlineAt ? moment(task.deadlineAt).format('HH:mm') : ''
										const titleShort = task.title.length > 20 ? `${task.title.slice(0, 20)}…` : task.title
										return (
											<div
												key={task.id}
												onClick={() => onOpenTask(task)}
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 4,
													fontSize: 11,
													cursor: 'pointer',
													padding: '1px 4px',
													borderRadius: 4,
													marginBottom: 2,
													background: '#f0f4ff',
													whiteSpace: 'nowrap',
													overflow: 'hidden'
												}}
											>
												{shouldShowFixedCompletionIcon(task) && (
													<Tooltip
														title={
															task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED
																? 'Úloha ukončená nesplením dátumu dodania → Otvoriť nasledujúcu úlohu'
																: 'Vznikla zo zmeškanej predchádzajúcej úlohy → Zobraziť zdroj'
														}
													>
														<FieldTimeOutlined
															style={{ color: '#fa8c16', fontSize: 12, marginRight: 4, cursor: 'pointer', flexShrink: 0 }}
															onClick={(e) => {
																e.stopPropagation()
																const targetId = task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED
																	? task.successorTaskId
																	: task.predecessorTaskId
																if (targetId) {
																	onOpenTask({ id: targetId } as ManagerTask)
																}
															}}
														/>
													</Tooltip>
												)}
												<span style={{
													width: 7, height: 7, borderRadius: '50%', background: dotColor, flexShrink: 0
												}} />
												<span style={{ color: '#6b7280', flexShrink: 0 }}>{timeLabel}</span>
												<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: '#1f2937' }}>{titleShort}</span>
											</div>
										)
									})}

									{visibleGhosts.map((ghost) => {
										const titleShort = ghost.title.length > 20 ? `${ghost.title.slice(0, 20)}…` : ghost.title
										const timeLabel = ghost.timeWindowFrom || ''
										return (
											<div
												key={ghost.id}
												title={'Kliknite pre vytvorenie inštancie tejto úlohy.'}
												onClick={() => onGhostClick(ghost)}
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: 4,
													fontSize: 11,
													cursor: 'pointer',
													padding: '1px 4px',
													borderRadius: 4,
													marginBottom: 2,
													background: '#f9fafb',
													border: '1px dashed #d1d5db',
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													opacity: 0.8
												}}
											>
												<span style={{
													width: 7, height: 7, borderRadius: '50%', background: '#9ca3af', flexShrink: 0
												}} />
												{timeLabel && <span style={{ color: '#9ca3af', flexShrink: 0 }}>{timeLabel}</span>}
												<span style={{ overflow: 'hidden', textOverflow: 'ellipsis', color: '#9ca3af' }}>{titleShort}</span>
											</div>
										)
									})}
									{(hiddenTaskCount > 0 || hasHiddenGhosts) && (
										<Typography.Text style={{
											display: 'block', fontSize: 11, color: '#6b7280', padding: '1px 4px'
										}}>
											{hiddenTaskCount > 0 ? `+${hiddenTaskCount} úloh` : ''}
											{hiddenTaskCount > 0 && hasHiddenGhosts ? ' · ' : ''}
											{hasHiddenGhosts ? 'Ďalšie opakovania' : ''}
										</Typography.Text>
									)}
								</div>
							)
						})}
					</div>
				))}
			</div>
		</Spin>
	)
})

export const formatPenaltyAmount = (penaltyAmount?: number | null) => (penaltyAmount ? `-${penaltyAmount} EUR` : '-')

export const getDeadlineState = (task: ManagerTask) => {
	if (!task.deadlineAt) {
		return { label: 'Bez termínu', color: '#6b7280', percent: 0 }
	}
	if (task.workflowStatus === MANAGER_TASK_WORKFLOW_STATUS.DONE) {
		return {
			label: task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED ? 'Dokončené po termíne' : 'Dokončené včas',
			color: task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED ? '#cf1322' : '#389e0d',
			percent: 100
		}
	}

	const deadline = moment(task.deadlineAt)
	const createdAt = task.createdAt ? moment(task.createdAt) : moment().subtract(1, 'hour')
	const totalMinutes = Math.max(deadline.diff(createdAt, 'minutes'), 1)
	const remainingMinutes = deadline.diff(moment(), 'minutes')

	if (remainingMinutes < 0) {
		const abs = Math.abs(remainingMinutes)
		let overdueLabel: string
		if (abs < 60) overdueLabel = `${abs} min`
		else if (abs < 1440) overdueLabel = `${Math.floor(abs / 60)} h`
		else overdueLabel = `${Math.floor(abs / 1440)} d`
		return { label: `Mešká ${overdueLabel}`, color: '#cf1322', percent: 100 }
	}

	return {
		label: remainingMinutes < 60 ? `${remainingMinutes} min` : `${Math.ceil(remainingMinutes / 60)} h`,
		color: remainingMinutes <= 240 ? '#fa8c16' : '#337ab7',
		percent: Math.min(100, Math.max(0, ((totalMinutes - remainingMinutes) / totalMinutes) * 100))
	}
}

export const isRecurringPattern = (value: string | null | undefined): value is 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY_DAY' => value === 'DAILY' || value === 'WEEKDAYS' || value === 'WEEKLY' || value === 'MONTHLY_DAY'

// ── Scope dialog: propagatable vs instance-only field classification ──────────

/** Fields that can propagate to the recurring template and trigger the scope dialog. */
export const PROPAGATABLE_RECURRING_FIELDS: readonly string[] = ['title', 'description', 'priority', 'penaltyAmount', 'labelIds']

/** Fields that are instance-scoped only and must never trigger the scope dialog. */
export const INSTANCE_ONLY_FIELDS: readonly string[] = ['workflowStatus', 'deadlineAt', 'startAt', 'assignedToUserId', 'reminderOffsetHours']

/**
 * Compare form values against the original task detail (or ghost template baseline) for
 * propagatable fields only. Returns the list of field names that changed.
 *
 * Normalisation rules:
 * - `description`: empty string and null are treated as equivalent.
 * - `penaltyAmount`: compare numeric value; null / 0 / undefined are treated as no penalty.
 * - `labelIds`: sort both arrays before comparing so order does not matter.
 */
export const getRecurringPropagatableChanges = (
	formValues: Record<string, any>,
	original: ManagerTask
): string[] => {
	const changed: string[] = []

	// title
	const formTitle = formValues.title ?? ''
	const origTitle = original.title ?? ''
	if (formTitle !== origTitle) changed.push('title')

	// description — empty string === null
	const formDesc = formValues.description || null
	const origDesc = original.description || null
	if (formDesc !== origDesc) changed.push('description')

	// priority
	const formPriority = formValues.priority ?? null
	const origPriority = original.priority ?? null
	if (formPriority !== origPriority) changed.push('priority')

	// penaltyAmount — null / 0 / undefined treated as "no penalty"
	const formPenalty = formValues.penalizaciaConfig?.penaltyAmount || null
	const origPenalty = original.penaltyAmount ? Number(original.penaltyAmount) : null
	if (formPenalty !== origPenalty) changed.push('penaltyAmount')

	// labelIds — sort both and compare
	const formLabelIds = [...(formValues.labelIds || [])].sort((a, b) => a - b)
	const origLabelIds = [...(original.labels?.map((l) => l.id) || [])].sort((a, b) => a - b)
	if (JSON.stringify(formLabelIds) !== JSON.stringify(origLabelIds)) changed.push('labelIds')

	// completionWindowHours — null and undefined are both "no window"
	const formHours = formValues.dateConfig?.completionWindowHours ?? null
	const origHours = original.completionWindowHours ?? null
	if (formHours !== origHours) changed.push('completionWindowHours')

	// fixedCompletion
	const formFixed = formValues.dateConfig?.fixedCompletion ?? false
	const origFixed = (original as any).fixedCompletion ?? false
	if (formFixed !== origFixed) changed.push('fixedCompletion')

	return changed
}

export const buildRecurrenceIntent = (
	values: any,
	editingTaskDetail: ManagerTask | null
): ManagerTaskRecurrenceIntent | undefined => {
	const cfg = values.dateConfig || {}
	const newPattern = cfg.recurringPattern || 'NEVER'
	const isEditing = Boolean(editingTaskDetail)
	const linkedTemplateId = editingTaskDetail?.recurringTemplateId
	const prevPattern = editingTaskDetail?.recurringPattern || 'NEVER'

	if (!isEditing) {
		// create mode
		if (isRecurringPattern(newPattern)) {
			return {
				action: 'UPSERT_TEMPLATE',
				pattern: newPattern,
				firstOccurrenceAt: cfg.deadlineAt ? cfg.deadlineAt.toISOString() : new Date().toISOString(),
				weekDays: newPattern === 'WEEKLY' && cfg.recurringWeekDays?.length ? cfg.recurringWeekDays as WeekDayKey[] : undefined,
				reminderOffsetHours: cfg.reminderOffsetHours ?? null
			}
		}
		return undefined
	}

	// edit mode
	if (linkedTemplateId) {
		// has linked template
		if (newPattern === 'NEVER') {
			return { action: 'DEACTIVATE_TEMPLATE' }
		}
		// check if recurrence fields changed
		const weekDaysChanged = newPattern === 'WEEKLY' && JSON.stringify(cfg.recurringWeekDays || []) !== JSON.stringify(editingTaskDetail?.recurringWeekDays || [])
		const patternChanged = newPattern !== prevPattern
		if (patternChanged || weekDaysChanged) {
			return {
				action: 'UPSERT_TEMPLATE',
				pattern: newPattern as 'DAILY' | 'WEEKDAYS' | 'WEEKLY' | 'MONTHLY_DAY',
				firstOccurrenceAt: cfg.deadlineAt ? cfg.deadlineAt.toISOString() : new Date().toISOString(),
				weekDays: newPattern === 'WEEKLY' && cfg.recurringWeekDays?.length ? cfg.recurringWeekDays as WeekDayKey[] : undefined,
				reminderOffsetHours: cfg.reminderOffsetHours ?? null
			}
		}
		return { action: 'UNCHANGED' }
	}

	// edit without linked template
	if (isRecurringPattern(newPattern) && !isRecurringPattern(prevPattern)) {
		return {
			action: 'UPSERT_TEMPLATE',
			pattern: newPattern,
			firstOccurrenceAt: cfg.deadlineAt ? cfg.deadlineAt.toISOString() : new Date().toISOString(),
			weekDays: newPattern === 'WEEKLY' && cfg.recurringWeekDays?.length ? cfg.recurringWeekDays as WeekDayKey[] : undefined,
			reminderOffsetHours: cfg.reminderOffsetHours ?? null
		}
	}

	return undefined
}

const buildDateConfigFromTask = (task: ManagerTask) => ({
	startAt: task.startAt ? moment(task.startAt) : null,
	deadlineAt: task.deadlineAt ? moment(task.deadlineAt) : null,
	recurringPattern: task.recurringPattern || 'NEVER',
	recurringWeekDays: task.recurringWeekDays || [],
	reminderOffsetHours: task.reminderOffsetHours ?? null,
	completionWindowHours: task.completionWindowHours ?? null,
	fixedCompletion: task.fixedCompletion ?? false
})

export const recurrencePatternFromTemplate = (template?: ManagerRecurringTaskTemplate | null): RecurringPattern | null => {
	if (!template) return null
	if (template.frequency === MANAGER_TASK_FREQUENCY.WEEKLY) return 'WEEKLY'
	if (template.frequency === MANAGER_TASK_FREQUENCY.MONTHLY) return 'MONTHLY_DAY'
	if (template.frequency === MANAGER_TASK_FREQUENCY.DAILY && template.workingDaysOnly) return 'WEEKDAYS'
	if (template.frequency === MANAGER_TASK_FREQUENCY.DAILY) return 'DAILY'
	return null
}

export const buildRecurringTemplateSchedulePayload = (dateConfig: any): Partial<ManagerRecurringTaskTemplatePayload> | null => {
	const pattern = dateConfig?.recurringPattern
	if (!isRecurringPattern(pattern)) return null
	let frequency = MANAGER_TASK_FREQUENCY.DAILY
	if (pattern === 'WEEKLY') {
		frequency = MANAGER_TASK_FREQUENCY.WEEKLY
	} else if (pattern === 'MONTHLY_DAY') {
		frequency = MANAGER_TASK_FREQUENCY.MONTHLY
	}

	return {
		frequency,
		workingDaysOnly: pattern === 'WEEKDAYS',
		weekDays: pattern === 'WEEKLY' ? JSON.stringify(dateConfig.recurringWeekDays || []) : null,
		monthDay: pattern === 'MONTHLY_DAY' && dateConfig.deadlineAt ? dateConfig.deadlineAt.date() : null,
		deadlineTime: dateConfig.deadlineAt ? dateConfig.deadlineAt.format('HH:mm:ss') : null
	}
}

const taskToPayload = (values: any, editingTaskDetail: ManagerTask | null = null): ManagerTaskPayload => {
	const cfg = values.dateConfig || {}
	const pen = values.penalizaciaConfig || {}
	const recurrenceIntent = buildRecurrenceIntent(values, editingTaskDetail)
	return {
		title: values.title,
		description: values.description || null,
		workflowStatus: values.workflowStatus,
		priority: values.priority || MANAGER_TASK_PRIORITY.LOW,
		assignedToUserId: values.assignedToUserId,
		startAt: cfg.startAt ? cfg.startAt.toISOString() : null,
		deadlineAt: cfg.deadlineAt ? cfg.deadlineAt.toISOString() : null,
		recurringPattern: cfg.recurringPattern && cfg.recurringPattern !== 'NEVER' ? cfg.recurringPattern : null,
		recurringWeekDays: cfg.recurringWeekDays?.length ? JSON.stringify(cfg.recurringWeekDays) : null,
		reminderOffsetHours: cfg.reminderOffsetHours ?? null,
		completionWindowHours: cfg.completionWindowHours ?? null,
		fixedCompletion: cfg.fixedCompletion ?? false,
		slaHours: null,
		penaltyAmount: pen.penaltyAmount || null,
		labelIds: values.labelIds || [],
		...(recurrenceIntent ? { recurrenceIntent } : {})
	}
}

const FieldSection = ({
	label,
	onRemove,
	children
}: {
	label: string
	onRemove: () => void
	children: React.ReactNode
}) => (
	<div style={{ marginBottom: 20 }}>
		<div style={{
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			marginBottom: 8
		}}>
			<span style={{
				fontSize: 13,
				fontWeight: 600,
				color: '#374151'
			}}>
				{label}
			</span>
			<button
				type={'button'}
				onClick={onRemove}
				style={{
					background: 'none',
					border: 'none',
					cursor: 'pointer',
					color: '#9ca3af',
					fontSize: 18,
					padding: '0 2px',
					lineHeight: 1
				}}
			>
				×
			</button>
		</div>
		{children}
	</div>
)

type FieldKey = 'labels' | 'dates' | 'priorita' | 'penalizacia'

const OPTIONAL_FIELD_CONFIGS: { key: FieldKey; label: string }[] = [
	{ key: 'priorita', label: 'Priorita' },
	{ key: 'penalizacia', label: 'Penalizácia' },
	{ key: 'labels', label: 'Labels' },
	{ key: 'dates', label: 'Dátum' }
]

const STATUS_SELECT_COLORS: Record<MANAGER_TASK_WORKFLOW_STATUS, { bg: string; color: string }> = {
	TODO: { bg: '#2563eb', color: '#fff' },
	IN_PROGRESS: { bg: '#d97706', color: '#fff' },
	DONE: { bg: '#16a34a', color: '#fff' },
	VOIDED: { bg: '#6b7280', color: '#fff' }
}

const StatusDropdown = ({ value, onChange }: { value?: MANAGER_TASK_WORKFLOW_STATUS; onChange?: (v: MANAGER_TASK_WORKFLOW_STATUS) => void }) => {
	const current = value || MANAGER_TASK_WORKFLOW_STATUS.TODO
	const colors = STATUS_SELECT_COLORS[current]
	const menu = (
		<Menu>
			{Object.values(MANAGER_TASK_WORKFLOW_STATUS).map((s) => (
				<Menu.Item key={s} onClick={() => onChange?.(s)}>
					{STATUS_LABELS[s]}
				</Menu.Item>
			))}
		</Menu>
	)
	return (
		<Dropdown overlay={menu} trigger={['click']}>
			<Button
				size={'small'}
				style={{
					background: colors.bg,
					color: colors.color,
					border: 'none',
					fontWeight: 600,
					fontSize: 13,
					borderRadius: 6,
					paddingLeft: 12,
					paddingRight: 10
				}}
			>
				{STATUS_LABELS[current]} <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
			</Button>
		</Dropdown>
	)
}

const ManagerTasks = () => {
	const profile = useSelector((state: RootState) => state.profile.profile.data)
	const [tasks, setTasks] = useState<ManagerTask[]>([])
	const [labels, setLabels] = useState<ManagerTaskLabel[]>([])
	const [recurringTemplates, setRecurringTemplates] = useState<ManagerRecurringTaskTemplate[]>([])
	const [openTemplateId, setOpenTemplateId] = useState<number | null>(null)
	const [mainTabKey, setMainTabKey] = useState<string>('list')
	const [assignees, setAssignees] = useState<AssigneeOption[]>([])
	const [selectedTask, setSelectedTask] = useState<ManagerTask | null>(null)
	const [isTaskModalOpen, setIsTaskModalOpen] = useState(false)
	const [editingTaskId, setEditingTaskId] = useState<number | null>(null)
	const [isDescEditing, setIsDescEditing] = useState(false)
	const [descBeforeEdit, setDescBeforeEdit] = useState<string>('')
	const [isFormDirty, setIsFormDirty] = useState(false)

	const [isLoading, setIsLoading] = useState(true)
	const [mainFilters, setMainFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS)
	const [activeFields, setActiveFields] = useState<Set<FieldKey>>(new Set())
	const [autoOpenFields, setAutoOpenFields] = useState<Set<FieldKey>>(new Set())
	const [taskLabelAssignments, setTaskLabelAssignments] = useState<Record<number, ManagerTaskLabelAssignmentData>>({})
	const [pendingEntityAssignments, setPendingEntityAssignments] = useState<Array<{ labelId: number; fkField: string; entityId: number; dataName: string }>>([])
	const [editingTaskDetail, setEditingTaskDetail] = useState<ManagerTask | null>(null)
	const [ghostPrefill, setGhostPrefill] = useState<{ templateId: number; occurrenceDate: string; occurrenceKey: string; recurringPattern?: RecurringPattern | null; recurringWeekDays?: WeekDayKey[] } | null>(null)
	const [autoFocusRecurrence, setAutoFocusRecurrence] = useState(false)
	const [templateScheduleEditActive, setTemplateScheduleEditActive] = useState(false)
	const queryClient = useQueryClient()
	const [taskForm] = Form.useForm()
	const [commentForm] = Form.useForm()
	const [modalCommentForm] = Form.useForm()
	const [penaltyEligible, setPenaltyEligible] = useState(false)
	const [descriptionDisplay, setDescriptionDisplay] = useState<string>('')

	const isAdmin = String(profile?.role) === 'ADMIN' || String(profile?.role) === 'SUPERADMIN'
	const canEditLabels = isAdmin || (profile as any)?.canEditManagerTaskLabels

	const loadAssignees = useCallback(async () => {
		const { data } = await getReq('/api/admin/users', { page: 1, limit: 500, allUserOrganizations: true })
		const raw = get(data, 'users', []) as Array<{ id?: number; uid?: number; firstName?: string; lastName?: string; email?: string; role?: string }>
		setAssignees(raw
			.map((user) => ({
				id: user.id || user.uid || 0,
				name: [user.lastName, user.firstName].filter(Boolean).join(' ') || user.email || `#${user.id || user.uid}`,
				role: user.role || ''
			}))
			.filter((user) => Boolean(user.id)))
	}, [])

	const loadAll = useCallback(async () => {
		setIsLoading(true)
		try {
			const [taskResult, labelResult, templateResult] = await Promise.all([
				getManagerTasks({
					limit: 100,
					...(mainFilters.assigneeId ? { assignedToUserId: mainFilters.assigneeId } : {}),
					...(mainFilters.dateRange ? {
						dateFrom: mainFilters.dateRange[0].toISOString(),
						dateTo: mainFilters.dateRange[1].toISOString()
					} : {})
				}),
				getManagerTaskLabels(),
				getRecurringTaskTemplates({ isActive: true })
			])
			setTasks(taskResult.data)
			setLabels(labelResult)
			setRecurringTemplates(templateResult)
		} finally {
			setIsLoading(false)
		}
	}, [mainFilters])

	// Client-side filtering for status (multi-select, backend only supports single)
	const displayedTasks = useMemo(() => {
		let result = tasks
		if (mainFilters.statuses.length > 0) {
			result = result.filter((t) => mainFilters.statuses.includes(t.workflowStatus))
		}
		return result
	}, [tasks, mainFilters.statuses])

	// Invalidate all cached calendar months — single prefix covers every month ever viewed.
	// Called after every mutation that can affect calendar data.
	const invalidateCalendar = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: [CALENDAR_QUERY_KEY] })
	}, [queryClient])

	useEffect(() => {
		loadAll()
	}, [loadAll])

	useEffect(() => {
		loadAssignees()
	}, [loadAssignees])

	const stats = useMemo(() => {
		const now = moment()
		const open = tasks.filter((task) => ![MANAGER_TASK_WORKFLOW_STATUS.DONE, MANAGER_TASK_WORKFLOW_STATUS.VOIDED].includes(task.workflowStatus))
		const overdue = open.filter((task) => task.deadlineAt && moment(task.deadlineAt).isBefore(now)).length
		const dueSoon = open.filter((task) => task.deadlineAt && moment(task.deadlineAt).diff(now, 'hours') <= 24 && moment(task.deadlineAt).isAfter(now)).length
		const penalties = tasks.reduce((sum, task) => {
			if (task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED) {
				return sum + Number(task.penaltyAmount || 0)
			}
			return sum
		}, 0)

		return {
			open: open.length, overdue, dueSoon, penalties
		}
	}, [tasks])

	const addField = (key: FieldKey) => {
		setActiveFields((prev) => new Set([...prev, key]))
		setAutoOpenFields((prev) => new Set([...prev, key]))
	}

	const removeField = (key: FieldKey) => {
		const clearMap: Partial<Record<FieldKey, object>> = {
			labels: { labelIds: [] },
			dates: { dateConfig: undefined },
			priorita: { priority: undefined },
			penalizacia: { penalizaciaConfig: undefined }
		}
		const toClear = clearMap[key]
		if (toClear) taskForm.setFieldsValue(toClear)
		setActiveFields((prev) => {
			const next = new Set(prev)
			next.delete(key)
			return next
		})
	}

	const inactiveFields = OPTIONAL_FIELD_CONFIGS.filter((config) => !activeFields.has(config.key))

	const openCreateTask = () => {
		taskForm.resetFields()
		taskForm.setFieldsValue({
			workflowStatus: MANAGER_TASK_WORKFLOW_STATUS.TODO,
			assignedToUserId: profile?.id,
			dateConfig: {
				completionWindowHours: null,
				fixedCompletion: false
			}
		})
		setDescriptionDisplay('')
		setIsFormDirty(false)
		setActiveFields(new Set())
		setEditingTaskId(null)
		setEditingTaskDetail(null)
		setIsDescEditing(false)
		setAutoOpenFields(new Set())
		setTaskLabelAssignments({})
		setPendingEntityAssignments([])
		setGhostPrefill(null)
		setAutoFocusRecurrence(false)
		setTemplateScheduleEditActive(false)
		setPenaltyEligible(false)
		setIsTaskModalOpen(true)
	}

	const openGhostTask = useCallback((ghost: CalendarGhostEvent) => {
		const template = recurringTemplates.find((item) => item.id === ghost.templateId)
		const recurringPattern = recurrencePatternFromTemplate(template)
		const labelIds = template?.labels?.map((label) => label.id) || []
		taskForm.resetFields()
		taskForm.setFieldsValue({
			title: ghost.title,
			workflowStatus: MANAGER_TASK_WORKFLOW_STATUS.TODO,
			priority: ghost.priority as MANAGER_TASK_PRIORITY,
			assignedToUserId: profile?.id,
			labelIds,
			dateConfig: {
				deadlineAt: moment(ghost.occurrenceDate).startOf('day'),
				startAt: null,
				recurringPattern: recurringPattern || 'NEVER',
				recurringWeekDays: parseWeekDays(template?.weekDays) as WeekDayKey[],
				reminderOffsetHours: null,
				completionWindowHours: template?.completionWindowHours ?? null,
				fixedCompletion: template?.fixedCompletion ?? false
			}
		})
		setDescriptionDisplay('')
		setIsFormDirty(false)
		setActiveFields(new Set<FieldKey>(labelIds.length ? ['priorita', 'dates', 'labels'] : ['priorita', 'dates']))
		setEditingTaskId(null)
		setEditingTaskDetail(null)
		setIsDescEditing(false)
		setAutoOpenFields(new Set())
		setTaskLabelAssignments({})
		setPendingEntityAssignments([])
		setAutoFocusRecurrence(false)
		setTemplateScheduleEditActive(false)
		setPenaltyEligible(!!(moment(ghost.occurrenceDate).isValid() || (template?.completionWindowHours ?? null) !== null))
		setGhostPrefill({
			templateId: ghost.templateId,
			occurrenceDate: ghost.occurrenceDate,
			occurrenceKey: ghost.occurrenceKey,
			recurringPattern,
			recurringWeekDays: parseWeekDays(template?.weekDays) as WeekDayKey[]
		})
		setIsTaskModalOpen(true)
	}, [profile, recurringTemplates, taskForm])

	const openEditTask = useCallback(async (task: ManagerTask) => {
		taskForm.resetFields()
		setIsFormDirty(false)
		setEditingTaskId(task.id)
		setIsTaskModalOpen(true)
		setIsDescEditing(false)

		// Load full detail
		const full = await getManagerTask(task.id)
		setEditingTaskDetail(full)

		// Detect which optional fields to show
		const nextFields = new Set<FieldKey>()
		if (full.labels?.length) nextFields.add('labels')
		if (full.deadlineAt || full.startAt) nextFields.add('dates')
		if (full.priority) nextFields.add('priorita')
		if (full.penaltyAmount) nextFields.add('penalizacia')
		setActiveFields(nextFields)
		setPenaltyEligible(!!(full.deadlineAt || full.completionWindowHours))
		setAutoOpenFields(new Set())

		// Build label assignment map for entity FK display
		const newAssignments: Record<number, ManagerTaskLabelAssignmentData> = {}
		full.labels?.forEach((l) => {
			if (l.managerTaskLabelAssignments) {
				newAssignments[l.id] = l.managerTaskLabelAssignments
			}
		})
		setTaskLabelAssignments(newAssignments)

		// Build dateConfig
		const dateConfig = (full.deadlineAt || full.startAt) ? buildDateConfigFromTask(full) : undefined

		// Build penalizaciaConfig
		const penalizaciaConfig = full.penaltyAmount ? {
			penaltyAmount: Number(full.penaltyAmount)
		} : undefined

		taskForm.setFieldsValue({
			title: full.title,
			description: full.description || '',
			workflowStatus: full.workflowStatus,
			priority: full.priority,
			assignedToUserId: full.assignedToUserId,
			labelIds: full.labels?.map((l) => l.id) || [],
			dateConfig,
			penalizaciaConfig
		})
		setDescriptionDisplay(full.description || '')
	}, [taskForm])

	const reloadLabelAssignments = useCallback(async () => {
		if (!editingTaskId) return
		const fresh = await getManagerTask(editingTaskId)
		const newAssignments: Record<number, ManagerTaskLabelAssignmentData> = {}
		fresh.labels?.forEach((l) => {
			if (l.managerTaskLabelAssignments) {
				newAssignments[l.id] = l.managerTaskLabelAssignments
			}
		})
		setTaskLabelAssignments(newAssignments)
	}, [editingTaskId])

	const handleCloseModal = () => {
		if (!isFormDirty) {
			setIsTaskModalOpen(false)
			setEditingTaskId(null)
			setGhostPrefill(null)
			setAutoFocusRecurrence(false)
			setTemplateScheduleEditActive(false)
			return
		}
		Modal.confirm({
			title: 'Zahodiť neuložené zmeny?',
			content: 'Máte rozpracované zmeny, ktoré neboli uložené. Ak odídete, príde o ne.',
			okText: 'Pokračovať v úprave',
			cancelText: 'Zahodiť zmeny',
			cancelButtonProps: { danger: true },
			onCancel: () => {
				setIsTaskModalOpen(false)
				setEditingTaskId(null)
				setGhostPrefill(null)
				setAutoFocusRecurrence(false)
				setTemplateScheduleEditActive(false)
			}
		})
	}

	const refreshSelectedTask = async (id: number) => {
		const nextTask = await getManagerTask(id)
		setSelectedTask(nextTask)
		setTasks((current) => current.map((task) => (task.id === id ? nextTask : task)))
	}

	const handleDateConfigChange = useCallback(async (nextDateConfig: any) => {
		setPenaltyEligible(!!(nextDateConfig?.deadlineAt || nextDateConfig?.completionWindowHours))
		// ── Ghost: frequency or completionWindowHours changed → confirm immediately, update template ──
		if (ghostPrefill && nextDateConfig?.recurringPattern !== 'NEVER') {
			const ghostTemplate = recurringTemplates.find((t) => t.id === ghostPrefill.templateId)
			const newPattern = nextDateConfig?.recurringPattern || 'NEVER'
			const oldPattern = ghostPrefill.recurringPattern || 'NEVER'
			const newDays = JSON.stringify([...(nextDateConfig?.recurringWeekDays || [])].sort())
			const oldDays = JSON.stringify([...parseWeekDays(ghostTemplate?.weekDays)].sort())
			const frequencyChanged = newPattern !== oldPattern || (newPattern === 'WEEKLY' && newDays !== oldDays)

			const oldHours = ghostTemplate?.completionWindowHours ?? null
			const newHours = (nextDateConfig?.completionWindowHours as number | null | undefined) ?? null
			const hoursChanged = oldHours !== newHours

			if (frequencyChanged || hoursChanged) {
				Modal.confirm({
					title: frequencyChanged ? 'Zmeniť frekvenciu opakovania?' : 'Zmeniť čas na splnenie?',
					content: 'Zmena sa prejaví u všetkých budúcich úloh tohto opakovania.',
					okText: 'Uložiť',
					cancelText: 'Zrušiť',
					okButtonProps: { type: 'primary' },
					onOk: async () => {
						try {
							const schedulePayload = frequencyChanged ? buildRecurringTemplateSchedulePayload(nextDateConfig) : null
							await updateRecurringTaskTemplate(ghostPrefill.templateId, {
								...(schedulePayload || {}),
								...(hoursChanged ? { completionWindowHours: newHours } : {})
							})
							message.success(frequencyChanged ? 'Frekvencia opakovania bola uložená' : 'Čas na splnenie bol uložený')
							invalidateCalendar()
							await loadAll()
							// Update ghostPrefill so the pill in the detail reflects the new pattern immediately
							if (frequencyChanged) {
								setGhostPrefill((prev) => {
									if (!prev) return prev
									return {
										...prev,
										recurringPattern: (nextDateConfig?.recurringPattern as RecurringPattern | null | undefined) ?? prev.recurringPattern,
										recurringWeekDays: (nextDateConfig?.recurringWeekDays as WeekDayKey[] | undefined) ?? prev.recurringWeekDays
									}
								})
							}
							setIsFormDirty(false)
						} catch {
							message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
						}
					},
					onCancel: () => {
						taskForm.setFieldsValue({
							dateConfig: {
								...nextDateConfig,
								...(frequencyChanged ? {
									recurringPattern: ghostPrefill.recurringPattern || 'NEVER',
									recurringWeekDays: parseWeekDays(ghostTemplate?.weekDays) as WeekDayKey[]
								} : {}),
								...(hoursChanged ? { completionWindowHours: oldHours } : {})
							}
						})
						setIsFormDirty(false)
					}
				})
				return
			}
		}

		// ── Ghost: user set recurrence to NEVER → offer to deactivate the template ──
		if (ghostPrefill && nextDateConfig?.recurringPattern === 'NEVER') {
			Modal.confirm({
				title: 'Odstrániť opakovanie?',
				content: 'Šablóna bude deaktivovaná a budúce opakovania sa prestanú generovať.',
				okText: 'Áno, odstrániť',
				cancelText: 'Zrušiť',
				onOk: async () => {
					try {
						await updateRecurringTaskTemplate(ghostPrefill.templateId, { isActive: false })
						message.success('Opakovanie bolo odstránené')
					} catch {
						message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
						return
					}
					setIsTaskModalOpen(false)
					setGhostPrefill(null)
					invalidateCalendar()
					await loadAll()
				}
			})
			return
		}

		// ── Linked instance with INACTIVE template: offer to reactivate on save ──
		if (!ghostPrefill && editingTaskDetail?.recurringTemplateId && nextDateConfig?.recurringPattern !== 'NEVER') {
			const isTemplateActive = recurringTemplates.some((t) => t.id === editingTaskDetail.recurringTemplateId)
			if (!isTemplateActive) {
				const schedulePayload = buildRecurringTemplateSchedulePayload(nextDateConfig)
				Modal.confirm({
					title: 'Aktivovať opakovanie?',
					content: 'Opakovanie sa aktivuje pre túto a všetky nasledujúce inštancie.',
					okText: 'Aktivovať',
					cancelText: 'Zrušiť',
					okButtonProps: { type: 'primary' },
					onOk: async () => {
						try {
							await updateRecurringTaskTemplate(editingTaskDetail.recurringTemplateId!, {
								isActive: true,
								...(schedulePayload || {}),
								completionWindowHours: (nextDateConfig?.completionWindowHours as number | null | undefined) ?? null
							})
							message.success('Opakovanie bolo aktivované')
							invalidateCalendar()
							await loadAll()
						} catch {
							message.error('Aktiváciu sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
						}
					},
					onCancel: () => {
						window.setTimeout(() => {
							taskForm.setFieldsValue({ dateConfig: buildDateConfigFromTask(editingTaskDetail) })
						}, 0)
					}
				})
				return
			}
		}

		// ── Linked instance: pill-triggered schedule edit ─────────────────────────
		if (!templateScheduleEditActive || !editingTaskDetail?.recurringTemplateId) {
			return
		}

		const templatePayload = buildRecurringTemplateSchedulePayload(nextDateConfig)
		if (!templatePayload) {
			setTemplateScheduleEditActive(false)
			setAutoFocusRecurrence(false)
			window.setTimeout(() => {
				taskForm.setFieldsValue({ dateConfig: buildDateConfigFromTask(editingTaskDetail) })
			}, 0)
			message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
			return
		}

		try {
			await updateRecurringTaskTemplate(editingTaskDetail.recurringTemplateId, {
				...templatePayload,
				completionWindowHours: (nextDateConfig?.completionWindowHours as number | null | undefined) ?? null,
				fixedCompletion: (nextDateConfig?.fixedCompletion as boolean | undefined) ?? false
			})
			message.success('Opakovanie šablóny bolo uložené')
			invalidateCalendar()
			await loadAll()
		} catch (err) {
			message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
		} finally {
			setTemplateScheduleEditActive(false)
			setAutoFocusRecurrence(false)
			window.setTimeout(() => {
				taskForm.setFieldsValue({ dateConfig: buildDateConfigFromTask(editingTaskDetail) })
			}, 0)
		}
	}, [editingTaskDetail, ghostPrefill, invalidateCalendar, loadAll, recurringTemplates, setGhostPrefill, setIsTaskModalOpen, setPenaltyEligible, taskForm, templateScheduleEditActive])

	const handleSaveTask = async () => {
		const values = await taskForm.validateFields()

		// ── Determine if scope dialog is needed ──────────────────────────────
		const isExistingRecurring = Boolean(editingTaskId && editingTaskDetail?.recurringTemplateId)
		const isGhostCreate = Boolean(!editingTaskId && ghostPrefill)

		// Build a "baseline" task for comparison when it's a ghost create.
		// completionWindowHours is read from the (already-refreshed) template so that an
		// immediate dialog in handleDateConfigChange that already saved it won't trigger again.
		const ghostTemplate = isGhostCreate && ghostPrefill
			? recurringTemplates.find((t) => t.id === ghostPrefill!.templateId)
			: undefined
		const ghostBaseline: ManagerTask | null = isGhostCreate && ghostPrefill
			? {
				id: 0,
				title: values.title,
				description: null,
				workflowStatus: MANAGER_TASK_WORKFLOW_STATUS.TODO,
				evaluationStatus: MANAGER_TASK_EVALUATION_STATUS.PENDING,
				priority: (values.priority ?? MANAGER_TASK_PRIORITY.LOW) as MANAGER_TASK_PRIORITY,
				assignedToUserId: values.assignedToUserId,
				penaltyAmount: null,
				completionWindowHours: ghostTemplate?.completionWindowHours ?? null,
				fixedCompletion: ghostTemplate?.fixedCompletion ?? false,
				labels: ghostTemplate?.labels || []
			}
			: null

		const comparisonBase = isExistingRecurring ? editingTaskDetail : ghostBaseline
		const propagatableChanges = comparisonBase ? getRecurringPropagatableChanges(values, comparisonBase) : []
		const needsScopeDialog = (isExistingRecurring || isGhostCreate) && propagatableChanges.length > 0

		// ── Shared save helpers ───────────────────────────────────────────────
		const finishSave = async () => {
			setIsTaskModalOpen(false)
			setEditingTaskId(null)
			setGhostPrefill(null)
			setAutoFocusRecurrence(false)
			setTemplateScheduleEditActive(false)
			invalidateCalendar()
			await loadAll()
		}

		const saveInstanceOnly = async () => {
			if (editingTaskId) {
				await updateManagerTask(editingTaskId, taskToPayload(values, editingTaskDetail))
				message.success('Úloha bola uložená')
			} else {
				const basePayload = taskToPayload(values, null)
				// Ghost creates must not send recurrenceIntent — the template already exists.
				// Sending UPSERT_TEMPLATE here would create a second template on the backend.
				if (ghostPrefill) { delete basePayload.recurrenceIntent }
				const created = await createManagerTask(ghostPrefill ? {
					...basePayload,
					recurringTemplateId: ghostPrefill!.templateId,
					occurrenceDate: ghostPrefill!.occurrenceDate,
					occurrenceKey: ghostPrefill!.occurrenceKey
				} : basePayload)
				if (pendingEntityAssignments.length > 0) {
					await Promise.all(
						pendingEntityAssignments.map(({
							labelId,
							fkField,
							entityId,
							dataName
						}) => assignManagerTaskLabelEntity(created.id, labelId, {
							[fkField]: entityId,
							dataName
						}))
					)
				}
				if (ghostPrefill) {
					// Propagate schedule (frequency/weekDays) to the existing template.
					// The user may have changed recurrence in the DatePickerPopover — this must
					// update the template rather than being silently dropped.
					const schedulePayload = buildRecurringTemplateSchedulePayload(values.dateConfig || {})
					if (schedulePayload) {
						await updateRecurringTaskTemplate(ghostPrefill.templateId, schedulePayload)
					}
					// finishSave will also call invalidateCalendar(); calling it here too is harmless
					// and ensures the ghost branch triggers an immediate invalidation before finishSave.
					invalidateCalendar()
				}
				message.success('Úloha bola vytvorená')
			}
			await finishSave()
		}

		const saveInstanceAndTemplate = async () => {
			const templateId = editingTaskDetail?.recurringTemplateId ?? ghostPrefill?.templateId
			if (!templateId) {
				await saveInstanceOnly()
				return
			}

			// Step 1: save instance
			let createdId: number | null = null
			if (editingTaskId) {
				await updateManagerTask(editingTaskId, taskToPayload(values, editingTaskDetail))
			} else {
				const basePayload = taskToPayload(values, null)
				// Same as saveInstanceOnly: strip recurrenceIntent so the existing template is not duplicated.
				if (ghostPrefill) { delete basePayload.recurrenceIntent }
				const created = await createManagerTask(ghostPrefill ? {
					...basePayload,
					recurringTemplateId: ghostPrefill!.templateId,
					occurrenceDate: ghostPrefill!.occurrenceDate,
					occurrenceKey: ghostPrefill!.occurrenceKey
				} : basePayload)
				createdId = created.id
				if (pendingEntityAssignments.length > 0) {
					await Promise.all(
						pendingEntityAssignments.map(({
							labelId,
							fkField,
							entityId,
							dataName
						}) => assignManagerTaskLabelEntity(created.id, labelId, {
							[fkField]: entityId,
							dataName
						}))
					)
				}
				if (ghostPrefill) {
					// finishSave will call invalidateCalendar(); explicit call here ensures
					// ghost-create path invalidates before the template propagation step too.
					invalidateCalendar()
				}
			}

			// Step 2: propagate to template
			const pen = values.penalizaciaConfig || {}
			// For ghost creates, also propagate schedule changes (frequency/weekDays) the user made
			// in the DatePickerPopover — these are not in PROPAGATABLE_RECURRING_FIELDS but belong
			// on the template, not the instance.
			const ghostSchedule = ghostPrefill ? (buildRecurringTemplateSchedulePayload(values.dateConfig || {}) || {}) : {}
			await updateRecurringTaskTemplate(templateId, {
				title: values.title,
				description: values.description || null,
				priority: values.priority ?? MANAGER_TASK_PRIORITY.LOW,
				penaltyAmount: pen.penaltyAmount || null,
				labelIds: values.labelIds || [],
				completionWindowHours: (values.dateConfig?.completionWindowHours) ?? null,
				fixedCompletion: values.dateConfig?.fixedCompletion ?? false,
				...ghostSchedule
			})

			message.success('Úloha aj budúce opakovania boli uložené')
			await finishSave()
		}

		// ── No scope dialog needed — save directly ────────────────────────────
		if (!needsScopeDialog) {
			try {
				await saveInstanceOnly()
			} catch {
				message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
			}
			return
		}

		// ── Show scope dialog ─────────────────────────────────────────────────
		Modal.confirm({
			title: 'Aplikovať zmeny pre:',
			content: 'Upravil si polia, ktoré môžu platiť aj pre budúce opakovania tejto úlohy.',
			okText: 'Túto úlohu',
			cancelText: 'Túto a budúce úlohy',
			// Neutral styling — do not use danger/primary for propagation
			okButtonProps: { type: 'default' },
			cancelButtonProps: { type: 'primary' },
			onOk: async () => {
				try {
					await saveInstanceOnly()
				} catch {
					message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
				}
			},
			onCancel: async (close) => {
				// Ant Design v4: onCancel is called for both the cancel button AND X/overlay dismiss.
				// We only want to propagate if the cancel button was explicitly clicked (close is a function).
				// When dismissed via X/overlay, close is undefined.
				if (typeof close === 'function') {
					try {
						await saveInstanceAndTemplate()
					} catch {
						message.error('Zmeny sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
					}
				}
				// If close is not a function (X/overlay dismiss), do nothing — returns to edit modal.
			}
		})
	}

	const handleStatusChange = async (task: ManagerTask, workflowStatus: MANAGER_TASK_WORKFLOW_STATUS) => {
		const updated = workflowStatus === MANAGER_TASK_WORKFLOW_STATUS.DONE
			? await completeManagerTask(task.id)
			: await updateManagerTask(task.id, { workflowStatus })
		invalidateCalendar()
		setTasks((current) => current.map((item) => (item.id === task.id ? updated : item)))
		if (selectedTask?.id === task.id) {
			setSelectedTask(updated)
		}
	}

	const handleDeleteTask = async (task: ManagerTask) => {
		await deleteManagerTask(task.id)
		message.success(isAdmin ? 'Úloha bola zrušená' : 'Úloha bola označená na zmazanie')
		invalidateCalendar()
		queryClient.invalidateQueries({ queryKey: ['managerTasks'] })
		setSelectedTask(null)
		await loadAll()
	}

	const handleAddComment = async () => {
		if (!selectedTask) return
		const values = await commentForm.validateFields()
		await addManagerTaskComment(selectedTask.id, values.body)
		commentForm.resetFields()
		await refreshSelectedTask(selectedTask.id)
	}

	const handleAddModalComment = async () => {
		if (!editingTaskId) return
		const values = await modalCommentForm.validateFields()
		await addManagerTaskComment(editingTaskId, values.body)
		modalCommentForm.resetFields()
		const fresh = await getManagerTask(editingTaskId)
		setEditingTaskDetail(fresh)
	}

	const runRecurringActions = async () => {
		await generateRecurringManagerTasks()
		await markOverdueManagerTasks()
		message.success('Opakované a omeškané úlohy boli prepočítané')
		invalidateCalendar()
		await loadAll()
	}

	const linkedRecurrencePattern = editingTaskDetail?.recurringTemplateId != null && isRecurringPattern(editingTaskDetail.recurringPattern)
		? editingTaskDetail.recurringPattern
		: null
	const ghostRecurrencePattern = ghostPrefill?.recurringPattern && isRecurringPattern(ghostPrefill.recurringPattern)
		? ghostPrefill.recurringPattern
		: null

	// Check whether the linked template is still active (recurringTemplates only contains isActive:true)
	const linkedTemplateActive = editingTaskDetail?.recurringTemplateId != null
		? recurringTemplates.some((t) => t.id === editingTaskDetail!.recurringTemplateId)
		: false

	// When a task instance was generated from a template but has no own recurrence (NEVER),
	// show the template info card inside DatePickerPopover instead of the recurrence select.
	let linkedTemplateForPopover: { id: number; title: string; recurringPattern: string; weekDays?: string[] } | undefined
	if (editingTaskDetail?.recurringTemplateId != null && !linkedRecurrencePattern) {
		const tpl = recurringTemplates.find((t) => t.id === editingTaskDetail.recurringTemplateId)
		if (tpl) {
			let weekDays: string[] = []
			if (tpl.weekDays) {
				try { weekDays = JSON.parse(tpl.weekDays) } catch { weekDays = tpl.weekDays.split(',').filter(Boolean) }
			}
			linkedTemplateForPopover = {
				id: tpl.id,
				title: tpl.title,
				frequency: tpl.frequency,
				workingDaysOnly: tpl.workingDaysOnly,
				weekDays,
				completionWindowHours: tpl.completionWindowHours
			}
		}
	}

	// Pill labels per D-02/D-04: 'Upraviť opakovanie šablóny' (linked) / 'Opakovanie šablóny' (ghost)
	let recurrencePill: { label: string; readOnly?: boolean; inactive?: boolean; onClick?: () => void } | undefined
	if (linkedRecurrencePattern) {
		const linkedDays = linkedRecurrencePattern === 'WEEKLY' && editingTaskDetail?.recurringWeekDays?.length
			? ` · ${formatWeekDaysSK(editingTaskDetail.recurringWeekDays as string[])}`
			: ''
		if (linkedTemplateActive) {
			recurrencePill = {
				label: `↻ ${RECURRENCE_PILL_LABELS[linkedRecurrencePattern]}${linkedDays}`,
				onClick: () => {
					setTemplateScheduleEditActive(true)
					setAutoFocusRecurrence(true)
				}
			}
		} else {
			// Template exists on the task but is no longer active — show grayed-out pill with reactivation dialog
			recurrencePill = {
				label: `↻ ${RECURRENCE_PILL_LABELS[linkedRecurrencePattern]}${linkedDays} (neaktívna)`,
				inactive: true,
				onClick: () => {
					Modal.confirm({
						title: 'Aktivovať opakovanie?',
						content: 'Frekvencia sa aktivuje pre túto a všetky nasledujúce inštancie.',
						okText: 'Aktivovať',
						cancelText: 'Zrušiť',
						okButtonProps: { type: 'primary' },
						onOk: async () => {
							try {
								await updateRecurringTaskTemplate(editingTaskDetail!.recurringTemplateId!, { isActive: true })
								message.success('Opakovanie bolo aktivované')
								await loadAll()
							} catch {
								message.error('Aktiváciu sa nepodarilo uložiť. Skontroluj pripojenie a skús to znova.')
							}
						}
					})
				}
			}
		}
	} else if (ghostRecurrencePattern) {
		const ghostDays = ghostRecurrencePattern === 'WEEKLY' && ghostPrefill?.recurringWeekDays?.length
			? ` · ${formatWeekDaysSK(ghostPrefill.recurringWeekDays as string[])}`
			: ''
		recurrencePill = {
			label: `↻ ${RECURRENCE_PILL_LABELS[ghostRecurrencePattern]}${ghostDays}`,
			readOnly: true
		}
	}

	const columns: ColumnsType<ManagerTask> = [
		{
			title: 'Úloha',
			dataIndex: 'title',
			key: 'title',
			render: (_value, task) => (
				<Space direction={'vertical'} size={2}>
					<Typography.Text strong>
						{task.title}
						{isRecurringTask(task) && <RecurringTaskIcon />}
					</Typography.Text>
					<Space size={4} wrap>
						<Tag color={STATUS_COLORS[task.workflowStatus]}>{STATUS_LABELS[task.workflowStatus]}</Tag>
						<Tag color={PRIORITY_COLORS[task.priority]}><PriorityIcon priority={task.priority} />{PRIORITY_LABELS[task.priority]}</Tag>
					</Space>
				</Space>
			)
		},
		{
			title: 'Zodpovedný',
			width: 180,
			render: (_value, task) => nameOf(task.assignedTo)
		},
		{
			title: 'Termín',
			width: 220,
			render: (_value, task) => {
				const deadlineState = getDeadlineState(task)
				return (
					<Space direction={'vertical'} style={{ width: '100%' }} size={2}>
						<Typography.Text style={{ color: deadlineState.color }}>{formatDate(task.deadlineAt)}</Typography.Text>
						<Progress percent={deadlineState.percent} showInfo={false} strokeColor={deadlineState.color} size={'small'} />
						<Typography.Text type={'secondary'} style={{ fontSize: 12 }}>{deadlineState.label}</Typography.Text>
					</Space>
				)
			}
		},
		{
			title: 'Vyhodnotenie',
			width: 140,
			render: (_value, task) => (
				<Tag color={EVALUATION_COLORS[task.evaluationStatus]}>
					{EVALUATION_LABELS[task.evaluationStatus]}
				</Tag>
			)
		},
		{
			title: 'Penále',
			width: 100,
			render: (_value, task) => formatPenaltyAmount(task.penaltyAmount)
		},
		{
			title: '',
			width: 150,
			render: (_value, task) => (
				<Space onClick={(event) => event.stopPropagation()}>
					<Select
						size={'small'}
						value={task.workflowStatus}
						style={{ width: 130 }}
						onChange={(value: MANAGER_TASK_WORKFLOW_STATUS) => handleStatusChange(task, value)}
					>
						{Object.values(MANAGER_TASK_WORKFLOW_STATUS).map((status) => (
							<Select.Option key={status} value={status}>{STATUS_LABELS[status]}</Select.Option>
						))}
					</Select>
				</Space>
			)
		}
	]

	const renderFixedCompletionOverlay = (task: ManagerTask) => {
		if (!shouldShowFixedCompletionIcon(task)) return null
		const isFailed = task.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED
		const tooltipTitle = isFailed
			? 'Úloha ukončená nesplením dátumu dodania → Otvoriť nasledujúcu úlohu'
			: 'Vznikla zo zmeškanej predchádzajúcej úlohy → Zobraziť zdroj'
		const targetId = isFailed ? task.successorTaskId : task.predecessorTaskId
		return (
			<Tooltip title={tooltipTitle}>
				<FieldTimeOutlined
					style={{
						position: 'absolute',
						top: 6,
						left: 6,
						color: '#fa8c16',
						fontSize: 14,
						cursor: targetId ? 'pointer' : 'default',
						zIndex: 1
					}}
					onClick={(e) => {
						e.stopPropagation()
						if (targetId) {
							openEditTask({ id: targetId } as ManagerTask)
						}
					}}
				/>
			</Tooltip>
		)
	}

	const renderKanbanColumn = (status: MANAGER_TASK_WORKFLOW_STATUS) => {
		const columnTasks = tasks.filter((task) => task.workflowStatus === status)
		return (
			<Col xs={24} lg={6} key={status}>
				<Card
					size={'small'}
					title={<Badge color={STATUS_COLORS[status] === 'default' ? '#8c8c8c' : undefined} text={`${STATUS_LABELS[status]} (${columnTasks.length})`} />}
					style={{ ...CARD_STYLE, minHeight: 420 }}
				>
					<Space direction={'vertical'} style={{ width: '100%' }}>
						{columnTasks.map((task) => {
							const deadlineState = getDeadlineState(task)
							return (
								<Card
									key={task.id}
									size={'small'}
									hoverable
									onClick={() => openEditTask(task)}
									style={{ borderRadius: 8, position: 'relative' }}
								>
									{renderFixedCompletionOverlay(task)}
									<Space direction={'vertical'} style={{ width: '100%' }} size={6}>
										<Typography.Text strong>
											{task.title}
											{isRecurringTask(task) && <RecurringTaskIcon />}
										</Typography.Text>
										<Space size={4} wrap>
											<Tag color={PRIORITY_COLORS[task.priority]}><PriorityIcon priority={task.priority} />{PRIORITY_LABELS[task.priority]}</Tag>
											{task.labels?.map((label) => <Tag key={label.id} color={label.color || 'default'}>{label.name}</Tag>)}
										</Space>
										<Typography.Text type={'secondary'}>{nameOf(task.assignedTo)}</Typography.Text>
										<Typography.Text style={{ color: deadlineState.color }}>{deadlineState.label}</Typography.Text>
									</Space>
								</Card>
							)
						})}
						{!columnTasks.length && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'Žiadne úlohy'} />}
					</Space>
				</Card>
			</Col>
		)
	}

	return (
		<div className={'page-wrapper'} style={PAGE_STYLE}>
			<Space direction={'vertical'} size={20} style={{ width: '100%' }}>
				<div style={{
					display: 'flex', justifyContent: 'space-between', gap: 20, alignItems: 'flex-start'
				}}>
					<div>
						<Typography.Title level={2} style={{ margin: 0, color: '#1f2937' }}>
							Manažérske úlohy
						</Typography.Title>
						<Typography.Text type={'secondary'} style={{ fontSize: 15 }}>
							SLA úlohy, opakované povinnosti, penále a audit aktivity pre manažérov.
						</Typography.Text>
					</div>
					<Space wrap>
						<Button icon={<ReloadOutlined />} onClick={() => { loadAll(); invalidateCalendar() }}>Obnoviť</Button>
						{isAdmin && <Button onClick={runRecurringActions}>Prepočítať SLA</Button>}
						<Button type={'primary'} icon={<PlusOutlined />} onClick={openCreateTask}>Nová úloha</Button>
					</Space>
				</div>

				<Row gutter={[16, 16]}>
					<Col xs={24} md={6}>
						<Card style={CARD_STYLE}><Statistic title={'Otvorené'} value={stats.open} prefix={<ClockCircleOutlined />} /></Card>
					</Col>
					<Col xs={24} md={6}>
						<Card style={CARD_STYLE}><Statistic title={'Meškajú'} value={stats.overdue} valueStyle={{ color: '#cf1322' }} prefix={<ExclamationCircleOutlined />} /></Card>
					</Col>
					<Col xs={24} md={6}>
						<Card style={CARD_STYLE}><Statistic title={'Do 24 h'} value={stats.dueSoon} valueStyle={{ color: '#fa8c16' }} /></Card>
					</Col>
					<Col xs={24} md={6}>
						<Card style={CARD_STYLE}><Statistic title={'Penále'} value={stats.penalties} suffix={'€'} valueStyle={{ color: stats.penalties ? '#cf1322' : '#389e0d' }} /></Card>
					</Col>
				</Row>

				<Card style={CARD_STYLE}>
					<Tabs activeKey={mainTabKey} onChange={setMainTabKey} destroyInactiveTabPane={false}>
						<Tabs.TabPane tab={'Zoznam'} key={'list'}>
							<div style={{ marginBottom: 12 }}>
								<TaskFilterBar
									filters={mainFilters}
									onChange={setMainFilters}
									assignees={assignees.map((a) => ({ id: a.id, email: a.name } as any))}
									currentUserId={profile?.id}
								/>
							</div>
							<Table
								rowKey={'id'}
								columns={columns}
								dataSource={displayedTasks}
								loading={isLoading}
								pagination={{ pageSize: 25 }}
								scroll={{ x: true }}
								onRow={(task) => ({ onClick: () => openEditTask(task) })}
								locale={{ emptyText: <Empty description={'Žiadne manažérske úlohy'} /> }}
							/>
						</Tabs.TabPane>
						<Tabs.TabPane tab={'Kanban'} key={'kanban'}>
							<div style={{ marginBottom: 12 }}>
								<TaskFilterBar
									filters={mainFilters}
									onChange={setMainFilters}
									assignees={assignees.map((a) => ({ id: a.id, email: a.name } as any))}
									currentUserId={profile?.id}
								/>
							</div>
							<Spin spinning={isLoading}>
								<Row gutter={[16, 16]}>
									{Object.values(MANAGER_TASK_WORKFLOW_STATUS).map(renderKanbanColumn)}
								</Row>
							</Spin>
						</Tabs.TabPane>
						<Tabs.TabPane tab={'Kalendár'} key={'calendar'}>
							<div style={{ marginBottom: 12 }}>
								<TaskFilterBar
									filters={mainFilters}
									onChange={setMainFilters}
									assignees={assignees.map((a) => ({ id: a.id, email: a.name } as any))}
									currentUserId={profile?.id}
								/>
							</div>
							<CalendarView onOpenTask={openEditTask} onGhostClick={openGhostTask} assigneeId={mainFilters.assigneeId} />
						</Tabs.TabPane>
						<Tabs.TabPane
							tab={
								<span>
									<SyncOutlined style={{ color: '#1890ff', marginRight: 4 }} />
									Plánované úlohy
								</span>
							}
							key={'templates'}
						>
							<RecurringTemplateManager
								assignees={assignees}
								currentUserId={profile?.id}
								templates={recurringTemplates}
								onTemplatesChange={setRecurringTemplates}
								onOpenEditTask={openEditTask}
								openTemplateId={openTemplateId}
								onOpenTemplateHandled={() => setOpenTemplateId(null)}
								onSaved={invalidateCalendar}
							/>
						</Tabs.TabPane>
					</Tabs>
				</Card>
			</Space>

			<Drawer
				visible={Boolean(selectedTask)}
				onClose={() => setSelectedTask(null)}
				width={560}
				title={selectedTask && (
					<span>
						{selectedTask.title}
						{isRecurringTask(selectedTask) && <RecurringTaskIcon />}
					</span>
				)}
			>
				<Spin spinning={false}>
					{selectedTask && (
						<Space direction={'vertical'} size={16} style={{ width: '100%' }}>
							<Space size={4} wrap>
								<Tag color={STATUS_COLORS[selectedTask.workflowStatus]}>{STATUS_LABELS[selectedTask.workflowStatus]}</Tag>
								<Tag color={PRIORITY_COLORS[selectedTask.priority]}><PriorityIcon priority={selectedTask.priority} />{PRIORITY_LABELS[selectedTask.priority]}</Tag>
								<Tag color={EVALUATION_COLORS[selectedTask.evaluationStatus]}>{EVALUATION_LABELS[selectedTask.evaluationStatus]}</Tag>
								{selectedTask.labels?.map((label) => <Tag key={label.id} color={label.color || 'default'}>{label.name}</Tag>)}
							</Space>
							<Card size={'small'} style={CARD_STYLE}>
								<Space direction={'vertical'} style={{ width: '100%' }}>
									<Typography.Text>{selectedTask.description || 'Bez popisu'}</Typography.Text>
									<Typography.Text type={'secondary'}>Zodpovedný: {nameOf(selectedTask.assignedTo)}</Typography.Text>
									<Typography.Text type={'secondary'}>Termín: {formatDate(selectedTask.deadlineAt)}</Typography.Text>
									<Typography.Text type={'secondary'}>SLA: {selectedTask.slaHours ? `${selectedTask.slaHours} h` : '-'}</Typography.Text>
									<Typography.Text type={'secondary'}>Penále: {formatPenaltyAmount(selectedTask.penaltyAmount)}</Typography.Text>
								</Space>
							</Card>
							<Space>
								{selectedTask.workflowStatus !== MANAGER_TASK_WORKFLOW_STATUS.DONE && (
									<Tooltip title={
										(selectedTask.fixedCompletion && selectedTask.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED)
											? 'Úloha je uzamknutá — pevné dokončenie po deadline'
											: ''
									}>
										<Button
											type={'primary'}
											icon={<CheckCircleOutlined />}
											disabled={!!(selectedTask.fixedCompletion && selectedTask.evaluationStatus === MANAGER_TASK_EVALUATION_STATUS.FAILED)}
											onClick={() => handleStatusChange(selectedTask, MANAGER_TASK_WORKFLOW_STATUS.DONE)}
										>
											Dokončiť
										</Button>
									</Tooltip>
								)}
								<Popconfirm
									title={'Zrušiť túto úlohu?'}
									onConfirm={() => handleDeleteTask(selectedTask)}
									okText={'Zrušiť úlohu'}
									cancelText={'Späť'}
								>
									<Button danger icon={<DeleteOutlined />}>Zrušiť</Button>
								</Popconfirm>
							</Space>
							<Card title={'Komentáre'} size={'small'} style={CARD_STYLE}>
								<Space direction={'vertical'} style={{ width: '100%' }}>
									{selectedTask.comments?.map((comment) => (
										<div key={comment.id} style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
											<Typography.Text strong>{nameOf(comment.author)}</Typography.Text>
											<Typography.Paragraph style={{ marginBottom: 2 }}>{comment.body}</Typography.Paragraph>
											<Typography.Text type={'secondary'} style={{ fontSize: 12 }}>{formatDate(comment.createdAt)}</Typography.Text>
										</div>
									))}
									<Form form={commentForm} layout={'vertical'}>
										<Form.Item name={'body'} rules={[{ required: true, message: 'Napíš komentár' }]}>
											<Input.TextArea rows={3} placeholder={'Komentár k úlohe'} />
										</Form.Item>
										<Button onClick={handleAddComment}>Pridať komentár</Button>
									</Form>
								</Space>
							</Card>
							<Card title={'Audit'} size={'small'} style={CARD_STYLE}>
								<Timeline>
									{selectedTask.activityLogs?.map((activity) => (
										<Timeline.Item key={activity.id}>
											<Typography.Text strong>{activity.action}</Typography.Text>
											<br />
											<Typography.Text type={'secondary'}>
												{nameOf(activity.actor)} · {formatDate(activity.createdAt)}
											</Typography.Text>
										</Timeline.Item>
									))}
								</Timeline>
							</Card>
						</Space>
					)}
				</Spin>
			</Drawer>

			<Modal
				visible={isTaskModalOpen}
				footer={null}
				closable={false}
				width={1100}
				bodyStyle={{ padding: 0, overflow: 'hidden' }}
				onCancel={handleCloseModal}
				destroyOnClose
			>
				<Form form={taskForm} layout={'vertical'} onValuesChange={() => setIsFormDirty(true)}>
					{/* ── Header ── */}
					<div style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '12px 20px',
						borderBottom: '1px solid #e5e7eb',
						background: '#fff'
					}}>
						<Form.Item noStyle name={'workflowStatus'}>
							<StatusDropdown />
						</Form.Item>
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							{(isAdmin || profile?.hasPermissionToRemoveManagerTask) && editingTaskId && (
								<Dropdown
									trigger={['click']}
									overlay={(
										<Menu>
											<Menu.Item
												key={'delete-task'}
												danger
												onClick={() => {
													Modal.confirm({
														title: 'Odstrániť úlohu?',
														content: 'Túto akciu nie je možné vrátiť späť.',
														okText: 'Odstrániť',
														okButtonProps: { danger: true },
														cancelText: 'Zrušiť',
														onOk: async () => {
															const deletedId = editingTaskId!
															await deleteManagerTask(deletedId)
															invalidateCalendar()
															queryClient.invalidateQueries({ queryKey: ['managerTasks'] })
															setIsTaskModalOpen(false)
															setEditingTaskId(null)
															setTasks((current) => current.filter((t) => t.id !== deletedId))
															message.success('Úloha bola odstránená')
														}
													})
												}}
											>
												Odstrániť úlohu
											</Menu.Item>
										</Menu>
									)}
								>
									<button
										type={'button'}
										style={{
											background: 'none',
											border: 'none',
											fontSize: 20,
											color: '#9ca3af',
											cursor: 'pointer',
											lineHeight: 1,
											padding: '0 4px'
										}}
									>
										<EllipsisOutlined />
									</button>
								</Dropdown>
							)}
							<button
								type={'button'}
								onClick={handleCloseModal}
								style={{
									background: 'none',
									border: 'none',
									fontSize: 22,
									color: '#9ca3af',
									cursor: 'pointer',
									lineHeight: 1,
									padding: '0 4px'
								}}
							>
								×
							</button>
						</div>
					</div>

					{/* ── Body: two columns ── */}
					<div style={{ display: 'flex', maxHeight: 660, overflow: 'hidden' }}>
						{/* Left column – form */}
						<div style={{
							flex: 2, overflowY: 'auto', padding: '24px 28px', borderRight: '1px solid #e5e7eb', background: '#fff'
						}}>
							{/* Title */}
							<Form.Item name={'title'} rules={[{ required: true, message: 'Zadaj názov' }]} style={{ marginBottom: 16 }}>
								<Input
									placeholder={'Názov úlohy...'}
									bordered={false}
									style={{
										fontSize: 22,
										fontWeight: 600,
										color: '#111827',
										borderBottom: '2px solid #e5e7eb',
										borderRadius: 0,
										padding: '0 0 8px',
										boxShadow: 'none'
									}}
								/>
							</Form.Item>

							{/* Dynamic fields toolbar */}
							{inactiveFields.length > 0 && (
								<div style={{
									display: 'flex',
									alignItems: 'center',
									gap: 6,
									marginBottom: 20,
									flexWrap: 'wrap'
								}}>
									{inactiveFields.map((config) => {
										const isPenalizacia = config.key === 'penalizacia'
										const penalizaciaDisabled = isPenalizacia && !penaltyEligible
										const btn = (
											<button
												key={config.key}
												type={'button'}
												disabled={penalizaciaDisabled}
												onClick={penalizaciaDisabled ? undefined : () => addField(config.key)}
												style={{
													display: 'inline-flex',
													alignItems: 'center',
													gap: 4,
													padding: '4px 12px',
													border: '1px solid #e5e7eb',
													borderRadius: 6,
													background: penalizaciaDisabled ? '#f3f4f6' : '#fff',
													color: penalizaciaDisabled ? '#d1d5db' : '#6b7280',
													fontSize: 13,
													cursor: penalizaciaDisabled ? 'not-allowed' : 'pointer',
													fontWeight: 500
												}}
											>
												+ {config.label}
											</button>
										)
										return penalizaciaDisabled ? (
											<Tooltip key={config.key} title={'Pre pridanie penalizácie je potrebné nastaviť dátum ukončenia alebo čas na splnenie úlohy.'}>
												{btn}
											</Tooltip>
										) : btn
									})}
								</div>
							)}

							{/* Labels – optional */}
							{activeFields.has('labels') && (
								<FieldSection label={'Labely'} onRemove={() => removeField('labels')}>
									<Form.Item noStyle name={'labelIds'}>
										<LabelPickerPopover
											labels={labels}
											canEdit={canEditLabels}
											taskId={editingTaskId ?? undefined}
											labelAssignments={taskLabelAssignments}
											autoOpen={autoOpenFields.has('labels')}
											onDismiss={() => removeField('labels')}
											onLabelsChanged={async () => {
												await loadAll()
												await reloadLabelAssignments()
											}}
											onPendingEntityAssignment={(labelId, fkField, entityId, dataName) => {
												setPendingEntityAssignments((prev) => [
													...prev.filter((p) => p.labelId !== labelId),
													{
														labelId, fkField, entityId, dataName
													}
												])
												// Also update local assignment map so the chip can resolve entityId as fallback
												setTaskLabelAssignments((prev) => ({
													...prev,
													[labelId]: { ...(prev[labelId] || {}), [fkField]: entityId, dataName } as ManagerTaskLabelAssignmentData
												}))
											}}
										/>
									</Form.Item>
								</FieldSection>
							)}

							{/* Dates – optional */}
							{activeFields.has('dates') && (
								<FieldSection label={'Termín'} onRemove={() => removeField('dates')}>
									{editingTaskDetail?.recurringTemplateId && (
										<div style={{
											background: '#fffbeb',
											border: '1px solid #f59e0b',
											borderRadius: 6,
											padding: '6px 10px',
											marginBottom: 8,
											fontSize: 12,
											color: '#92400e'
										}}>
											Táto úloha vznikla z opakovanej šablóny. Zmeny tejto úlohy platia len pre túto inštanciu. Budúce opakovania upravíš cez Opakovanie.
										</div>
									)}
									<Form.Item
										noStyle
										name={'dateConfig'}
										rules={[{
											validator: (_, v) => (v?.deadlineAt || (v?.recurringPattern && v.recurringPattern !== 'NEVER')
												? Promise.resolve()
												: Promise.reject(new Error('Dátum splnenia je povinný')))
										}]}
									>
										<DatePickerPopover
											autoOpen={autoOpenFields.has('dates')}
											recurrencePill={recurrencePill}
											autoFocusRecurrence={autoFocusRecurrence}
											onAutoFocusRecurrenceHandled={() => setAutoFocusRecurrence(false)}
											onChange={handleDateConfigChange}
											onDismiss={() => {
												setTemplateScheduleEditActive(false)
												setAutoFocusRecurrence(false)
												const cfg = taskForm.getFieldValue('dateConfig')
												if (!cfg?.deadlineAt && !cfg?.startAt && (!cfg?.recurringPattern || cfg.recurringPattern === 'NEVER')) {
													removeField('dates')
												}
											}}
											onRemove={() => removeField('dates')}
											linkedTemplate={linkedTemplateForPopover}
											onOpenTemplate={linkedTemplateForPopover ? () => {
												setMainTabKey('templates')
												setOpenTemplateId(linkedTemplateForPopover.id)
											} : undefined}
										/>
									</Form.Item>
								</FieldSection>
							)}

							{/* Description */}
							<div style={{ marginBottom: 20 }}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
									<div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>📝 Popis</div>
									{!isDescEditing && (
										<Button size={'small'} onClick={() => { setDescBeforeEdit(descriptionDisplay); setIsDescEditing(true) }}>Upraviť</Button>
									)}
								</div>
								<Form.Item noStyle name={'description'}>
									<Input.TextArea
										rows={5}
										placeholder={'Pridajte detailnejší popis...'}
										autoFocus={isDescEditing}
										style={{ borderRadius: 6, display: isDescEditing ? undefined : 'none' }}
										onChange={(e) => setDescriptionDisplay(e.target.value)}
									/>
								</Form.Item>
								{isDescEditing ? (
									<div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
										<Button size={'small'} type={'primary'} onClick={() => setIsDescEditing(false)}>Uložiť</Button>
										<Button size={'small'} onClick={() => { taskForm.setFieldsValue({ description: descBeforeEdit }); setDescriptionDisplay(descBeforeEdit); setIsDescEditing(false) }}>Zrušiť</Button>
									</div>
								) : (
									<Typography.Text style={{ color: descriptionDisplay ? '#374151' : '#9ca3af', fontSize: 14, whiteSpace: 'pre-wrap' }}>
										{descriptionDisplay || 'Žiadny popis'}
									</Typography.Text>
								)}
							</div>

							{/* Zadávateľ – static, only for existing tasks */}
							{editingTaskDetail?.createdBy && (
								<div style={{
									marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8
								}}>
									<span style={{ fontSize: 12, color: '#9ca3af' }}>Zadávateľ:</span>
									<span style={{ fontSize: 13, color: '#374151', fontWeight: 500 }}>{nameOf(editingTaskDetail.createdBy)}</span>
								</div>
							)}

							{/* Pridelené – always visible */}
							<Form.Item name={'assignedToUserId'} label={'Pridelené'} rules={[{ required: true, message: 'Vyber zodpovedného' }]}>
								<Select showSearch optionFilterProp={'children'} placeholder={'Vyber manažéra...'}>
									{assignees.map((assignee) => (
										<Select.Option key={assignee.id} value={assignee.id}>{assignee.name}</Select.Option>
									))}
								</Select>
							</Form.Item>

							{/* Priorita – optional */}
							{activeFields.has('priorita') && (
								<FieldSection label={'Priorita'} onRemove={() => removeField('priorita')}>
									<Form.Item noStyle name={'priority'}>
										<SimplePickerPopover
											placeholder={'Priorita'}
											options={Object.values(MANAGER_TASK_PRIORITY).filter((p) => p !== 'MEDIUM').map((p) => ({
												value: p,
												label: PRIORITY_LABELS[p],
												icon: <PriorityIcon priority={p} />
											}))}
											autoOpen={autoOpenFields.has('priorita')}
											onDismiss={() => {
												if (!taskForm.getFieldValue('priority')) removeField('priorita')
											}}
										/>
									</Form.Item>
								</FieldSection>
							)}

							{/* Penalizácia + SLA – optional */}
							{activeFields.has('penalizacia') && (
								<FieldSection label={'Penalizácia pri meškaní'} onRemove={() => removeField('penalizacia')}>
									<Form.Item noStyle name={'penalizaciaConfig'}>
										<PenalizaciaPopover
											autoOpen={autoOpenFields.has('penalizacia')}
											onDismiss={() => {
												const cfg = taskForm.getFieldValue('penalizaciaConfig')
												if (!cfg?.penaltyAmount && !cfg?.slaHours) removeField('penalizacia')
											}}
											onRemove={() => removeField('penalizacia')}
										/>
									</Form.Item>
								</FieldSection>
							)}
						</div>

						{/* Right column – comments placeholder */}
						<div style={{
							flex: 1,
							background: '#f9fafb',
							padding: '20px 16px',
							display: 'flex',
							flexDirection: 'column',
							overflowY: 'auto'
						}}>
							<h4 style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 16 }}>
								💬 Komentáre a aktivita
							</h4>
							{!editingTaskId ? (
								<div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 13, textAlign: 'center', gap: 8 }}>
									<span style={{ fontSize: 28 }}>💬</span>
									<span>Komentáre budú dostupné<br />po vytvorení úlohy.</span>
								</div>
							) : (
								<div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto' }}>
									{/* Comment input */}
									<Form form={modalCommentForm} layout={'vertical'}>
										<Form.Item name={'body'} rules={[{ required: true, message: 'Napíš komentár' }]} style={{ marginBottom: 8 }}>
											<Input.TextArea rows={2} placeholder={'Pridaj komentár…'} />
										</Form.Item>
										<Button size={'small'} onClick={handleAddModalComment}>Pridať komentár</Button>
									</Form>

									{/* Comments list */}
									{(editingTaskDetail?.comments?.length ?? 0) > 0 && (
										<div>
											<div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Komentáre</div>
											<Space direction={'vertical'} style={{ width: '100%' }} size={8}>
												{editingTaskDetail!.comments!.map((comment) => (
													<div key={comment.id} style={{ background: '#fff', borderRadius: 8, padding: '8px 12px', border: '1px solid #e5e7eb' }}>
														<div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
															<Typography.Text strong style={{ fontSize: 12 }}>{nameOf(comment.author)}</Typography.Text>
															<Typography.Text type={'secondary'} style={{ fontSize: 11 }}>{formatDate(comment.createdAt)}</Typography.Text>
														</div>
														<Typography.Text style={{ fontSize: 13 }}>{comment.body}</Typography.Text>
													</div>
												))}
											</Space>
										</div>
									)}

									{/* Activity log */}
									{(editingTaskDetail?.activityLogs?.length ?? 0) > 0 && (
										<div>
											<div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aktivita</div>
											<Timeline style={{ paddingLeft: 4 }}>
												{editingTaskDetail!.activityLogs!.map((log) => {
													const detail = renderActivityDetail(log.action, log.metadata)
													return (
														<Timeline.Item key={log.id} style={{ paddingBottom: 8 }}>
															<Typography.Text strong style={{ fontSize: 12 }}>
																{ACTIVITY_ACTION_LABELS[log.action] ?? log.action}
															</Typography.Text>
															{detail && (
																<div style={{ fontSize: 11, color: '#4b5563', marginTop: 2 }}>{detail}</div>
															)}
															<div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
																{nameOf(log.actor)} · {formatDate(log.createdAt)}
															</div>
														</Timeline.Item>
													)
												})}
											</Timeline>
										</div>
									)}

									{(editingTaskDetail?.comments?.length ?? 0) === 0 && (editingTaskDetail?.activityLogs?.length ?? 0) === 0 && (
										<div style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', marginTop: 16 }}>Zatiaľ žiadna aktivita.</div>
									)}
								</div>
							)}
						</div>
					</div>

					{/* ── Footer ── */}
					<div style={{
						display: 'flex',
						justifyContent: 'flex-end',
						gap: 12,
						padding: '12px 20px',
						borderTop: '1px solid #e5e7eb',
						background: '#fff'
					}}>
						<Button onClick={handleCloseModal}>Zrušiť</Button>
						{/* eslint-disable-next-line no-nested-ternary */}
						<Button type={'primary'} onClick={handleSaveTask}>{editingTaskId ? 'Uložiť zmeny' : ghostPrefill ? 'Upraviť úlohu' : 'Vytvoriť úlohu'}</Button>
					</div>
				</Form>
			</Modal>

		</div>
	)
}

export default ManagerTasks
